// The list lives inside a SwipeableDrawer rather than a popup, so it is a
// styled vertical navigation column, not a Menu opening/closing surface —
// `@gto/ui/menu`'s Root/Popup/Item compound does not model that. It reuses the
// DS row spec via `menuItemVariants` so the rows match menu rows without
// re-deriving their metrics, and supplies its own `:hover` state, since the
// variant's highlight keys off Base UI's `data-highlighted`, which nothing
// sets here.
import { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getRouteApi } from '@tanstack/react-router'

import { cn } from '@gto/ui/cn'
import { Divider } from '@gto/ui/divider'
import { Drawer } from '@gto/ui/drawer'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { IconButton } from '@gto/ui/icon-button'
import { Bars3Icon } from '@gto/ui/icons/outline'
import {
  ArrowLeftEndOnRectangleIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  UserIcon,
} from '@gto/ui/icons/solid'
import { menuItemVariants } from '@gto/ui/menu'
import { Typography } from '@gto/ui/typography'

import { useAuth } from '@/hooks/use-auth'

// `menuItemVariants` is a 20px popup-row spec; this drawer is the only nav on a
// phone, so the rows are re-heighted to MUI MenuItem's mobile 48px minimum and
// its 16px side padding. Below 44px they stop being reliable touch targets.
const navRowMetricsClassName = cn(menuItemVariants(), 'h-auto min-h-12 justify-start px-4 py-3')

const navRowClassName = cn(
  navRowMetricsClassName,
  'transition-colors duration-300 hover:text-primary-main [&:hover_svg]:text-primary-main',
)

const routeApi = getRouteApi('/_layout/_private')

export const HeaderNavigationMobile = () => {
  const { formatMessage } = useIntl()
  const navigate = routeApi.useNavigate()
  const isSmAndLower = useMediaQuery('down', 800)
  const { isAuthorized, user, onLogout } = useAuth()

  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  const onToggleDrawer = () => {
    setOpen((prev) => !prev)
  }

  if (!isSmAndLower || !isAuthorized || !user) {
    return null
  }

  return (
    <>
      <IconButton
        ref={anchorRef}
        edge="start"
        onClick={onToggleDrawer}
      >
        <Bars3Icon />
      </IconButton>

      <Drawer.Root
        open={open}
        swipeDirection="left"
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setOpen(false)
          }
        }}
      >
        <Drawer.Portal>
          <Drawer.Backdrop />
          <Drawer.Popup
            aria-label={formatMessage({ id: 'common.labels.menu' })}
            className="mb-2 ml-2 mt-[64px] h-[calc(100dvh-72px)] w-[70%] max-w-[280px] rounded-md border border-divider bg-paper-elevation2 md:ml-6 sm:ml-4"
            side="left"
          >
            <nav
              className="flex h-full flex-col overflow-y-auto text-text-secondary"
              onClick={onToggleDrawer}
            >
              {/* Identity display, not an action — a row, not a button. */}
              <div className={cn(navRowMetricsClassName, 'cursor-default')}>
                <UserCircleIcon />
                <div className="flex flex-col items-start">
                  <Typography
                    className="mb-0 leading-[1]"
                    color="text.primary"
                    variant="subtitle1"
                  >
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="body3"
                  >
                    {user.email}
                  </Typography>
                </div>
              </div>

              <Divider />
              <button
                className={navRowClassName}
                type="button"
                onClick={() => {
                  navigate({
                    to: '/app/rewards',
                  }).catch(console.error)
                }}
              >
                <FormattedMessage id="common.labels.myRewards" />
              </button>
              <button
                className={navRowClassName}
                type="button"
                onClick={() => {
                  navigate({
                    to: '/app/deals',
                  }).catch(console.error)
                }}
              >
                <FormattedMessage id="common.labels.pokerDeals" />
              </button>
              <button
                className={navRowClassName}
                type="button"
                onClick={() => {
                  navigate({
                    to: '/faq',
                  }).catch(console.error)
                }}
              >
                <FormattedMessage id="common.controls.faq" />
              </button>
              <button
                className={navRowClassName}
                type="button"
                onClick={() => {
                  navigate({
                    to: '/support',
                  }).catch(console.error)
                }}
              >
                {' '}
                <FormattedMessage id="common.controls.support" />
              </button>
              <Divider />

              <button
                className={navRowClassName}
                type="button"
                onClick={() => {
                  navigate({
                    to: '/app/user/account',
                  }).catch(console.error)
                }}
              >
                <UserIcon />
                <FormattedMessage id="common.controls.account" />
              </button>
              <button
                className={navRowClassName}
                type="button"
                onClick={() => {
                  navigate({
                    to: '/app/user/settings',
                  }).catch(console.error)
                }}
              >
                <Cog6ToothIcon />
                <FormattedMessage id="common.controls.settings" />
              </button>
              <div className="flex flex-[1_0_auto] flex-col justify-end">
                <Divider />
                <button
                  className={navRowClassName}
                  type="button"
                  onClick={() => {
                    onLogout()
                  }}
                >
                  <ArrowLeftEndOnRectangleIcon />
                  Logout
                </button>
              </div>
            </nav>
          </Drawer.Popup>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  )
}
